import {
  FunctionComponent,
  memo,
  PropsWithChildren,
  ReactNode,
  useEffect,
} from 'react';

import { useSelector, shallowEqual } from 'react-redux';

import { useAppData } from 'src/app/data';
import { RouteComponentProps } from 'src/router/types';
import { MenuItem } from 'src/components/navigation';
import { getRouteFeaturesFromAppData } from 'src/router/utils';
import Navigation, {
  useLegacyReduxNavigationData,
} from 'src/sections/global/containers/navigation-v2';
import { memoRepoLink } from 'src/sections/repository/components/navigation-repo-link';
import {
  getCurrentRepository,
  getCurrentRepositoryLinkData,
  getIsCurrentRepositoryPrivate,
} from 'src/selectors/repository-selectors';
import { BucketState } from 'src/types/state';
import { useRepositoryNavigationProps } from './use-repository-navigation-props';
import { useRepositoryNavigationEffects } from './use-repository-navigation-effects';
import { useFlag } from 'src/hooks/flag';
import { useContainerNavigationProps } from 'src/hooks/use-container-navigation-props';
import { useNavigationData } from 'src/hooks/use-navigation-data';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

type RepositoryNavigationProps = RouteComponentProps & {
  children: ReactNode;
  isBeingRenderedInsideMobileNav: boolean;
  menuItems: MenuItem[];
  selectedMenuItem: MenuItem | undefined;
};

export const RepositoryNavigation = memo((props: RepositoryNavigationProps) => {
  useRepositoryNavigationEffects();

  const appData = useAppData();
  const { isNavigationResourceSwitchEnabled } =
    getRouteFeaturesFromAppData(appData);
  const navigationData = useNavigationData();

  const legacyNavigationData = useLegacyReduxNavigationData();

  const { showFlagComponent, dismissFlag } = useFlag();

  const currentRepositoryLinkData = useSelector(
    getCurrentRepositoryLinkData,
    shallowEqual
  );
  const isCurrentRepositoryPrivate = useSelector(getIsCurrentRepositoryPrivate);

  const isRepoMigrationFlagEnabled = useStatsigGate(
    StatsigFeatureKeys.repoMigrationFlag
  );

  const containerBitbucketActions = useSelector(
    (state: BucketState) => state.repository.section.bitbucketActions
  );
  const containerConnectActions = useSelector(
    (state: BucketState) => state.repository.section.connectActions
  );
  const { name, links, landingPage, fullName } = currentRepositoryLinkData;
  const htmlUrl = links?.html.href;
  const avatarUrl = links?.avatar.href;
  const hasRepo = !!name && !!fullName && !!htmlUrl;
  const RepoLink: FunctionComponent<PropsWithChildren<any>> = memoRepoLink(
    name,
    fullName,
    landingPage,
    htmlUrl
  );
  const headerLinkComponent = hasRepo ? RepoLink : undefined;

  const { repoName, repoSlug, repoMaintenanceMode } = useSelector(
    (state: BucketState) => {
      const currentRepository = getCurrentRepository(state);
      return {
        repoName: currentRepository ? currentRepository.name : undefined,
        repoSlug: currentRepository ? currentRepository.slug : undefined,
        repoMaintenanceMode: currentRepository
          ? currentRepository.maintenance_mode
          : undefined,
      };
    },
    shallowEqual
  );

  useEffect(() => {
    if (repoMaintenanceMode && isRepoMigrationFlagEnabled) {
      showFlagComponent('repo-maintenance-flag');
    }
    return () => {
      if (repoMaintenanceMode) {
        dismissFlag('repo-maintenance-flag');
      }
    };
  }, [
    repoMaintenanceMode,
    dismissFlag,
    isRepoMigrationFlagEnabled,
    showFlagComponent,
  ]);

  if (isNavigationResourceSwitchEnabled) {
    return (
      <Navigation
        {...navigationData}
        isGlobalContext={false}
        navigationType="container"
        isBeingRenderedInsideMobileNav={props.isBeingRenderedInsideMobileNav}
        createDrawerHeaderLinkComponent={headerLinkComponent}
        useContainerNavigationProps={useContainerNavigationProps}
      >
        {props.children}
      </Navigation>
    );
  }

  return (
    <Navigation
      {...legacyNavigationData}
      isBeingRenderedInsideMobileNav={props.isBeingRenderedInsideMobileNav}
      containerBitbucketActions={containerBitbucketActions}
      containerConnectActions={containerConnectActions}
      containerName={name}
      containerHref={htmlUrl}
      containerLogo={avatarUrl}
      createDrawerHeaderLinkComponent={headerLinkComponent}
      isGlobalContext={false}
      isPrivate={isCurrentRepositoryPrivate}
      navigationType="container"
      repoName={repoName}
      repoSlug={repoSlug}
      useContainerNavigationProps={useRepositoryNavigationProps}
    >
      {props.children}
    </Navigation>
  );
});
